<template>
  <main>
    <Loader :loader="formLoader" label="Carregando..." loaderType="progress" />

    <section class="md:my-28 mt-3 grid gap-y-5" v-if="!formLoader">
      <p class="text-xl md:text-2xl font-medium px-1">{{ $t("course.form.add") }}</p>
      <div class="grid grid-cols-12 gap-5">
        <div class="p-2 col-span-12 md:col-span-9">
          <form @submit.prevent="submitCourse()" autocomplete="off">
            <p class="text-lg font-bold">{{ $t("course.form.subtitle") }}</p>

            <div v-if="!isEditable" class="flex flex-col gap-3 my-4">
              <p class="mb-2">
                {{
                  $t("labels.generic", {
                    pt: "Tipo de curso",
                    en: "Course type",
                  })
                }}
              </p>
              <div :class="`flex gap-9 items-center ${v$.form.courseType.$error ? 'border border-red-500 p-3' : ''
                } rounded-md w-fit`">
                <div v-for="(item, i) in radios" :key="i">
                  <div class="flex items-center gap-3">
                    <div id="btn-radio">
                      <RadioButton id="radio-adaptive" v-model="form.courseType" :inputId="item.name"
                        :value="item.value" :disabled="item.disabled" name="courseType" class="custom-radio" />
                      <label :for="item.name" :class="`ml-2 cursor-pointer ${item.disabled ? 'text-gray-400' : ''
                        }`">
                        {{ item.name }}
                      </label>
                    </div>
                    <span v-tooltip.bottom="`
                          ${item.value == 'ADAPTATIVE'
                        ? $t('course.form.types.adaptive')
                        : item.value == 'LINEAR'
                          ? $t('course.form.types.traditional')
                          : item.value == 'INTERACTIVE'
                            ? $t('course.form.types.interactive')
                            : ''
                      }
                        `
                      ">
                      <v-icon name="md-info-outlined" class="text-h-primary-500" />
                    </span>
                  </div>
                </div>
              </div>
              <Errors :errors="v$.form.courseType.$errors" />
            </div>

            <div v-if="form.courseType === 'INTERACTIVE'" class="flex flex-col my-4">
              <p class="mb-2">
              <p class="mb-2">
                {{ $t("course.form.materialInteractive") }}
              </p>

              <div class="w-4/12 mb-3">
                <AutoComplete id="predecessor-input" v-model="form.course.material" :suggestions="materials"
                  :forceSelection="true" :placeholder="$t('course.form.materialInteractiveDescription')"
                  optionLabel="title" :class="[
                    'w-full',
                    v$.form.course.material.$error ? 'p-invalid' : '',
                  ]" @complete="searchForMaterials($event)" dropdown />
                <Errors :errors="v$.form.course.material.$errors" />
              </div>

              <small>
                {{ 'Para adicionar materiais ' }}
                <a href="https://wp.homero.app.br/wp-admin/admin.php?page=h5p" target="_blank" class="text-h-primary-500 font-bold cursor-pointer">{{ 'Clique aqui' }} </a>
              </small>
              </p>
            </div>

            <div class="flex flex-col gap-3 my-4">
              <p class="mb-2">
                {{ $t("course.form.hasCertificate") }}
              </p>
              <div id="btn-radio" class="flex gap-5">
                <div v-for="(item, i) in certificate" :key="i">
                  <div class="flex items-center">
                    <RadioButton id="radio-certificate" v-model="form.course.hasCertificate" :inputId="item.name"
                      :value="item.value" name="hasCertificate" class="custom-radio" />
                    <label :for="item.name" :class="`ml-2 cursor-pointer`">
                      {{ item.name }}
                    </label>
                  </div>
                </div>
              </div>

              <div id="certificate" class="my-3" v-if="form.course.hasCertificate">
                <p class="mb-2">
                  {{ $t("course.form.certificateType") }}
                </p>

                <div class="md:w-4/12">
                  <AutoComplete id="predecessor-input" v-model="form.course.certificate" :suggestions="certificates"
                    :forceSelection="true" :placeholder="$t('course.form.certificatePlaceholder')" optionLabel="name"
                    :class="[
                      'w-full',
                      v$.form.course.certificate.$error ? 'p-invalid' : '',
                    ]" @complete="searchForCertificates($event)" dropdown />
                  <Errors :errors="v$.form.course.certificate.$errors" />
                </div>
              </div>
            </div>

            <div class="flex flex-col my-4">
              <p class="mb-2">{{ $t("course.form.name") }}</p>
              <InputText id="name-input" type="text" v-model="form.course.name"
                :class="v$.form.course.name.$error ? 'p-invalid' : ''" :maxlength="40" />
              <Errors :errors="v$.form.course.name.$errors" :actualLength="form.course.name?.length" :maxLength="40" />
            </div>
            <div class="flex flex-col my-4">
              <p class="mb-2">{{ $t("course.form.duration") }}</p>
              <InputMask id="name-input" type="text" v-model="form.course.duration" mask="999:99:99"
                placeholder="00:00:00" :class="v$.form.course.duration.$error ? 'p-invalid' : ''" />
              <Errors :errors="v$.form.course.duration.$errors" :actualLength="form.course.duration?.length" />
            </div>

            <div class="flex flex-col my-4" id="dropzone-files">
              <p class="mb-2">{{ $t("course.form.thumb") }}</p>
              <Dropzone :files="form.files" :errors="v$.form.files.$errors" :acceptFiles="`.jpg,.jpeg,.png`"
                @onThumbnailView="loadThumbnail" @onRemoveThumbnailView="removeThumbnail" />
            </div>

            <div class="flex flex-col my-4">
              <p class="mb-2">{{ $t("course.form.categories") }}</p>
              <AutoComplete id="categories-input" optionLabel="name" v-model="form.course.categories"
                :suggestions="autocomplete.sugestions" :multiple="true" :forceSelection="true" :delay="500"
                :class="v$.form.course.categories.$error ? 'p-invalid' : ''" @complete="searchForCategories($event)"
                @keyup.enter="addNewCategories($event)" />
              <Errors :errors="v$.form.course.categories.$errors" />
            </div>

            <div class="flex flex-col my-4">
              <p class="mb-2">{{ $t("course.form.description") }}</p>
              <Textarea id="description-input" :autoResize="true" rows="5" cols="30" v-model="form.course.description"
                :class="v$.form.course.description.$error ? 'p-invalid' : ''" :maxlength="200" />
              <Errors :errors="v$.form.course.description.$errors" :actualLength="form.course.description?.length"
                :maxLength="200" />
            </div>

            <div class="flex flex-col my-4">
              <p class="mb-2">
                {{
                  $t("labels.generic", {
                    pt: "Ementa ",
                    en: "Summary",
                  })
                }}
              </p>
              <Editor id="summary-input" v-model="form.course.summary" editorStyle="height: 400px" :class="v$.form.course.summary.$error
                ? 'border border-red-500 rounded-md'
                : ''
                " />

              <Errors :errors="v$.form.course.summary.$errors" :actualLength="form.course.summary?.length"
                :maxLength="5000" />
            </div>

            <div class="flex items-center gap-3 col-span-12 mt-10">
              <Button id="cancel-btn" label="Cancelar" class="btn-outline" @click="confirmModal()" />
              <Button id="submit-btn" type="submit" label="Salvar" class="btn-primary" />
            </div>
          </form>
        </div>
        <div class="p-2 col-span-3 hidden md:flex">
          <CardView id="card-course" :thumbnail="viewThumbnail" :title="form.course.name"
            :description="form.course.description" :categories="form.course.categories" />
        </div>
      </div>

      <ConfirmDialog />
    </section>
  </main>
</template>

<script lang="ts">
import { maxLength, required } from "@/locale/locale.config";
import { Certificate } from "@/models/certificate.model";
import { Course, CourseType } from "@/models/courses.model";
import { QueryParams } from "@/models/query-params.model";
import { CertificateRest } from "@/services/rest/certificate.rest";
import { CoursesRest } from "@/services/rest/courses.rest";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import HandleError from "@/utils/handle-errors.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import useVuelidate from "@vuelidate/core";
import { defineComponent } from "vue";
import { CourseService } from "./course.service";

export default defineComponent({
  name: "formCourse",
  computed: {
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    rest(): CoursesRest {
      return new CoursesRest();
    },
    service(): CourseService {
      return new CourseService();
    },
    errors(): HandleError {
      return new HandleError();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    certificate(): CertificateRest {
      return new CertificateRest();
    },
  },
  data() {
    return {
      form: {
        course: new Course(),
        files: [] as any,
        courseType: null,
      } as any,
      autocomplete: {
        sugestions: [] as any,
      },
      viewThumbnail: null as any,
      emptyImg:
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png" as string,
      isEditable: this.$route.params.id ?? "",
      formLoader: false as boolean,
      catValue: "",
      radios: [
        {
          name: this.$t("labels.generic", { pt: "Adaptativo", en: "Adaptive" }),
          value: CourseType.ADAPTATIVE,
          disabled: false,
        },
        {
          name: this.$t("labels.generic", {
            pt: "Tradicional",
            en: "Traditional",
          }),
          value: CourseType.LINEAR,
          disabled: false,
        },
        {
          name: this.$t("labels.generic", {
            pt: "Interativo",
            en: "Interactive",
          }),
          value: CourseType.INTERACTIVE,
          disabled: false,
        },
      ] as any[],
      certificate: [
        {
          name: this.$t("labels.generic", { pt: "Sim", en: "Yes" }),
          value: true,
        },
        {
          name: this.$t("labels.generic", { pt: "Não", en: "No" }),
          value: false,
        },
      ] as { name: string; value: boolean }[],
      certificates: [] as Certificate[],
      materials: [] as any[],
      params: {} as Partial<QueryParams>,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        course: {
          name: { required },
          duration: { required },
          description: { required },
          summary: { required, max: maxLength(5000) },
          categories: { required },
          certificate:
            this.form.course.hasCertificate === true ? { required } : "",
          material:
            this.form.courseType === CourseType.INTERACTIVE ? { required } : "",
        },
        files: { required },
        courseType: this.isEditable ? "" : { required },
      } as any,
    };
  },
  mounted() {
    if (this.isEditable) this.getCourseById();
    this.form.courseType = this.radios[0].value;
    this.form.course.hasCertificate = this.certificate[0].value;

    this.searchForCertificates({ query: "" });
  },
  watch: {
    "form.course.hasCertificate"() {
      this.v$.$reset();
      this.form.course.certificate = null;
    },
  },
  methods: {
    confirmModal(): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: "Deseja sair da tela de cadastro do curso?",
          en: "Do you want to exit the course registration screen?",
        }),
        header: this.$t("labels.generic", {
          pt: "Sair da tela",
          en: "Exit Screen",
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Sair",
          en: "Exit",
        }),
        rejectLabel: this.$t("labels.no"),
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.go.redirectTo("dashboardTeacher");
        },
      });
    },
    submitCourse(): void {
      const userId: string = getUserFromStorage()?.id;
      this.v$.form.$touch();
      if (!this.v$.form.$error) {
        const data = new Course();
        data.id = this.form.course?.id ?? undefined;
        data.name = this.form.course.name;
        data.description = this.form.course.description;
        data.summary = this.form.course.summary;
        data.teacher = userId;
        data.categories = this.form.course.categories.map((e: any) => e.name);
        data.hasCertificate = this.form.course.hasCertificate ?? true;

        this.sendCertificate(data);

        data.duration = this.generics.stringToSeconds(
          String(this.form.course.duration)
        );

        data.type = this.form.courseType;

        if (data.type === CourseType.INTERACTIVE) {
          data.h5pId = this.form.course.material?.id ?? null;
        }

        const appendName: any = { file: "file", data: "course" };

        const formData: FormData = this.generics.fileBuild(
          data,
          this.form.files[0],
          appendName
        );

        if (this.isEditable) this.updateCourse(formData);
        else this.createCourse(formData);

      }
    },
    sendCertificate(data): void {
      if (this.form.course.hasCertificate) {
        data.certificateId = this.form.course.certificate?.id;
        data.certificateName = this.form.course.certificate?.name;
      } else {
        data.certificateId = undefined;
        data.certificateName = undefined;
      }
    },
    createCourse(data: any): void {
      this.formLoader = true;
      this.rest.postCourse(data).subscribe({
        next: (response) => {
          this.$toast.add(ToastFactory.success(Messages.COURSE_ADDED));
          this.go.redirectToPage("chapters", { id: response.id, h5pId: response?.h5p?.h5pId, type: response?.type === CourseType.INTERACTIVE ? response.type : undefined });
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    updateCourse(data: any): void {
      const courseId: any = this.$route.params.id;
      this.formLoader = true;
      this.rest.updateCourse(courseId, data).subscribe({
        next: (response) => {
          this.$toast.add(ToastFactory.success(Messages.COURSE_UPDATED));
          this.go.redirectToPage("chapters", { id: response.id, h5pId: response?.h5p?.h5pId, type: response?.type === CourseType.INTERACTIVE ? response.type : undefined });
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    getCourseById(): void {
      const courseId: any = this.$route.params.id;
      this.service.course.subscribe({
        next: (response) => {
          this.form.course = response;
          this.form.course.categories = response.categories ?? [];
          this.form.files = new Array(response.file);
          this.viewThumbnail = response.file?.url;
          this.form.course.certificate = {
            id: response.certificateId ?? "",
            name: response.certificateName ?? "",
            template: response.certificateTemplate ?? "",
          };
        },
        error: () => {
          this.$router.go(-1);
        },
      });
      this.service.getCoursesById(courseId);
    },
    loadThumbnail(event: any): void {
      const fileRead = new FileReader();
      fileRead.onload = () => (this.viewThumbnail = fileRead.result);
      fileRead.readAsDataURL(event);
    },
    removeThumbnail(): void {
      this.viewThumbnail = null;
    },
    imgDefaultError(e: any): void {
      e.target.src =
        "https://video-thumb.s3.sa-east-1.amazonaws.com/65197bdbplaceholder.png";
    },
    searchForCategories(event: any): void {
      const { query } = event;
      this.service.categories.subscribe({
        next: (response) => {
          this.autocomplete.sugestions = response.filter((e: any) => {
            const isAlready = this.generics.isAlreadyExists(
              e,
              this.form.course?.categories
            );
            return !isAlready ? e?.name?.toLowerCase() : null;
          });
        },
      });
      this.service.getCategories({
        page: 0,
        size: 100,
        name: query.toLowerCase(),
      });
    },
    addNewCategories(event: any): void {
      var { value } = event.target;
      var obj: any = { name: value?.toUpperCase() };
      if (value?.length !== 0) {
        var filtered = this.form.course?.categories?.filter(
          (e: any) => e?.name == value
        );
        if (filtered?.length == 1) {
          this.$toast.add(ToastFactory.warn(Messages.CATEGORIE_DUPLICATED));
        } else this.form.course.categories?.push(obj);

        event.target.value = "";
      }
    },
    searchForCertificates(event: { query: string }): void {
      this.params.name = event.query.toLowerCase();
      CertificateRest.getAll(this.params).subscribe({
        next: (response) => (this.certificates = response.content),
        complete: () => { },
      });
    },
    searchForMaterials(event: { query: string }): void {
      this.params.name = event.query.toLowerCase();
      MaterialsRest.getAllInteractiveMaterials(this.params).subscribe({
        next: (response) => (this.materials = response.content),
        complete: () => { },
      });
    },
  },
});
</script>

<style>
.p-autocomplete-dd .p-autocomplete-dropdown {
  @apply bg-h-primary-500 !important;
}
</style>
